import { Preloader } from "@smartsuite/react-ui/lib";
import type React from "react";
import { type FC, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { FormErrorMessage } from "../../components/FormErrorMessage/FormErrorMessage";
import { FormNoPermissions } from "../../components/FormNoPermissions/FormNoPermissions";
import { FormNotFoundMessage } from "../../components/FormNotFoundMessage/FormNotFoundMessage";
import { FormPasscodeRequired } from "../../components/FormPasscodeRequired/FormPasscodeRequired";
import { PageContentBox } from "../../components/PageContentBox/PageContentBox";
import { useIsPreview } from "../../hooks/useIsPreview";
import { usePasscodeContext } from "../../hooks/usePasscodeContext";
import { FileStackProvider } from "../../providers/FileStackProvider";
import { PasscodeProvider } from "../../providers/PasscodeProvider";
import { buildLoginRedirectUrl } from "../../utils/auth";
import { env } from "../../utils/env";
import { $t } from "../../utils/intl";
import { navigateTo } from "../../utils/navigation";
import { FormDetail } from "./FormDetail";
import { FormPageText } from "./FormPage.text";
import { useFetchFormConfig } from "./hooks/useFetchFormConfig";
import { useFetchInternalFormConfig } from "./hooks/useFetchInternalFormConfig";
import { useFormStyleImageUrl } from "./hooks/useFormStyleImageUrl";
import { useLogoUrl } from "./hooks/useLogoUrl";
import { defineTitle, isFooterVisible } from "./utils";

export const FormPageRoute: FC = () => {
  const { accountId, hash } = useParams();
  const { isPreview } = useIsPreview();

  if (!accountId || !hash) {
    return null;
  }

  return <FormPage accountId={accountId} hash={hash} isPreview={isPreview} />;
};

interface FormPageProps {
  accountId: string;
  hash: string;
  isPreview?: boolean;
}

const FormPageContent: FC<FormPageProps> = ({ accountId, hash, isPreview = false }) => {
  const { passcode, setPasscode } = usePasscodeContext();
  const { data, isLoading, statusCode } = useFetchFormConfig({
    accountId,
    hash,
    isPreview,
    passcode,
  });

  const logoUrl = useLogoUrl(data?.form_state?.logo_handle);
  const bannerImageUrl = useFormStyleImageUrl(data?.form_state?.style?.banner_image?.handle);
  const fullImageUrl = useFormStyleImageUrl(data?.form_state?.style?.full_image?.handle);

  const hasMissingApplication = Boolean(data && !data.application);
  const isNotFound = statusCode === 404 || hasMissingApplication;
  const isPasscodeForm = [401, 403].includes(statusCode ?? 0);
  // Everything else that is neither a rendered form nor one of the screens above
  // (402 for an inactive plan, 5xx, a network failure) used to render nothing at all.
  const hasUnhandledError = !isLoading && !isNotFound && !isPasscodeForm && statusCode !== 200;

  const isError = useMemo(() => Boolean(passcode) && statusCode !== 200, [statusCode, passcode]);

  useEffect(() => {
    document.title = defineTitle(statusCode, data?.form_state?.tab_title);
  }, [statusCode, data?.form_state?.tab_title]);

  return (
    <PageContentBox
      formState={data?.form_state}
      featureFlags={data?.feature_flags}
      isPaidPlan={data?.has_active_paid_subscription}
      logoUrl={isLoading ? null : logoUrl}
      bannerImageUrl={bannerImageUrl}
      fullImageUrl={fullImageUrl}
      displayBranding={isFooterVisible(isLoading, data?.display_smartsuite_branding)}
      solutionColor={data?.solution_color}
    >
      {isLoading && (
        <div style={{ position: "relative", padding: "50px 0" }}>
          <Preloader />
        </div>
      )}

      {isNotFound && <FormNotFoundMessage />}
      {hasUnhandledError && <FormErrorMessage statusCode={statusCode} />}
      {isPasscodeForm && (
        <FormPasscodeRequired
          title={$t(FormPageText.passcodeTitle)}
          isError={isError}
          onSubmit={(passcode) => setPasscode(passcode)}
        />
      )}

      {data?.application && !isPasscodeForm && (
        <FormDetail
          accountId={accountId}
          hash={hash}
          config={data}
          isPreview={isPreview}
          passcode={passcode}
        />
      )}
    </PageContentBox>
  );
};

const FormFileStackProvider: FC<FormPageProps & { children?: React.ReactNode }> = (props) => {
  const { passcode } = usePasscodeContext();
  const { data } = useFetchFormConfig({ ...props, passcode });

  return (
    <FileStackProvider apiKey={data?.filestack_api_key} security={data?.file_security}>
      <FormPageContent {...props} />
    </FileStackProvider>
  );
};

export const FormPage: FC<FormPageProps> = (props) => {
  return (
    <PasscodeProvider>
      <FormFileStackProvider {...props} />
    </PasscodeProvider>
  );
};

export const InternalFormPageRoute: React.FC = () => {
  const { accountId = "", reportId = "" } = useParams<{ accountId: string; reportId: string }>();
  return <InternalFormPage accountId={accountId} reportId={reportId} />;
};

interface InternalFormPageProps {
  accountId: string;
  reportId: string;
}

const InternalFormPage: React.FC<InternalFormPageProps> = ({ accountId, reportId }) => {
  return <InternalFormFileStackProvider accountId={accountId} reportId={reportId} />;
};

const InternalFormFileStackProvider: React.FC<InternalFormPageProps> = ({
  accountId,
  reportId,
}) => {
  const { formConfig } = useFetchInternalFormConfig({ accountId, reportId });

  return (
    <FileStackProvider apiKey={formConfig?.filestack_api_key} security={formConfig?.file_security}>
      <InternalFormPageContent accountId={accountId} reportId={reportId} />
    </FileStackProvider>
  );
};

const InternalFormPageContent: React.FC<InternalFormPageProps> = ({ accountId, reportId }) => {
  const { formConfig, statusCode, isLoading } = useFetchInternalFormConfig({ accountId, reportId });
  const logoUrl = useLogoUrl(formConfig?.form_state?.logo_handle ?? null);
  const bannerImageUrl = useFormStyleImageUrl(formConfig?.form_state?.style?.banner_image?.handle);
  const fullImageUrl = useFormStyleImageUrl(formConfig?.form_state?.style?.full_image?.handle);

  useEffect(() => {
    if (formConfig?.form_state?.tab_title) {
      document.title = formConfig.form_state.tab_title;
    }
  }, [formConfig?.form_state?.tab_title]);

  useEffect(() => {
    if (statusCode === 401) {
      navigateTo(buildLoginRedirectUrl(env.REACT_APP_SMARTSUITE_APP_URL, window.location.pathname));
    }
  }, [statusCode]);

  if (statusCode === 403) {
    return <FormNoPermissions />;
  }

  return (
    <PageContentBox
      formState={formConfig?.form_state}
      featureFlags={formConfig?.feature_flags}
      isPaidPlan={formConfig?.has_active_paid_subscription}
      logoUrl={isLoading ? null : logoUrl}
      bannerImageUrl={bannerImageUrl}
      fullImageUrl={fullImageUrl}
      displayBranding={isFooterVisible(isLoading, formConfig?.display_smartsuite_branding)}
      solutionColor={formConfig?.solution_color}
    >
      {isLoading && (
        <div style={{ position: "relative", padding: "50px 0" }}>
          <Preloader />
        </div>
      )}

      {(statusCode === 404 ||
        (!isLoading && !formConfig && statusCode !== 401) ||
        (formConfig && !formConfig.application)) && <FormNotFoundMessage />}
      {statusCode === 401 && null}

      {formConfig?.application && statusCode !== 401 && (
        <FormDetail
          config={formConfig}
          accountId={accountId}
          isInternal={true}
          reportId={reportId}
        />
      )}
    </PageContentBox>
  );
};
